import { Badge } from '@/components/ui';

/**
 * Module-level shared DataTable cell renderers.
 * These are defined outside React components so they are stable references
 * and do not trigger SonarCloud S6478 (inline cell definitions).
 */

/**
 * Coerce an unknown scalar to a display string. The `typeof` guards narrow the
 * type so neither S6551 (object default-stringification — objects are
 * JSON-encoded) nor S4325 (no `as` cast is needed) fires.
 */
export function coerceScalarToString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    // Guard JSON.stringify: loosely-typed table data may contain circular
    // refs (or BigInt) that would throw and crash the cell render.
    try {
      return JSON.stringify(value) ?? '';
    } catch {
      return '[unserializable]';
    }
  }
  // Call String() only on a positively-narrowed primitive so S6551 (which does
  // not track the exclusions above) sees a non-object type; symbol/function are
  // not displayable values, so they collapse to ''.
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  return '';
}

/** Renders a string value or a muted dash span when the value is falsy. */
export function renderOptionalStringCell(value: unknown) {
  if (!value) {
    return <span className="text-muted-foreground">-</span>;
  }
  return coerceScalarToString(value);
}

/** Renders a "Synced" (success) or "Manual" (secondary) badge based on a boolean value. */
export function renderFabricSyncedCell(value: unknown) {
  return value ? (
    <Badge variant="success">Synced</Badge>
  ) : (
    <Badge variant="secondary">Manual</Badge>
  );
}

/**
 * Renders an "Archived" (warning) or "Active" (success) badge.
 * Used for entity status columns (regions, teams, products).
 */
export function renderArchivedEntityCell(value: unknown) {
  return value ? (
    <Badge variant="warning">Archived</Badge>
  ) : (
    <Badge variant="success">Active</Badge>
  );
}

/**
 * Renders an "Archived" (secondary) or "Active" (success) badge.
 * Used for Jira project status columns.
 */
export function renderJiraArchivedCell(value: unknown) {
  return (
    <Badge variant={value ? 'secondary' : 'success'}>
      {value ? 'Archived' : 'Active'}
    </Badge>
  );
}

/** Renders scalar values consistently in raw table pages. */
export function renderRawScalarCell(value: unknown) {
  if (value === null || value === undefined) {
    return '-';
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  if (typeof value === 'string') {
    return value;
  }
  // Positively narrow before String() so S6551 sees a non-object type; the
  // remaining symbol/function values are not meaningful scalar cells.
  if (typeof value === 'number' || typeof value === 'bigint') {
    return String(value);
  }
  return '';
}
